import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import axios from "axios";
import { MAIN_URL } from "../../../Configurations/Urls";
import { buildCoreSessionUser } from "../../../Configurations/coreSession";
import { Eye, EyeOff } from "lucide-react";
import AuthShell from "../../../Components/Auth/AuthShell";

const SpinSVG = () => (
  <svg width="18" height="18" viewBox="0 0 15 15" fill="none"
    style={{ animation: "spin 0.8s linear infinite", flexShrink: 0 }}>
    <circle cx="7.5" cy="7.5" r="5.5" stroke="currentColor" strokeOpacity=".3" strokeWidth="2.5" fill="none" />
    <path d="M7.5 2A5.5 5.5 0 0 1 13 7.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" />
  </svg>
);

function Login({
  title = "Core Admin Login",
  appName = "HR ERP Core",
  description,
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});

  const [loading, setLoading] = useState(false);

  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { isLoggedIn, login, setAccess } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    const savedEmail = localStorage.getItem("savedEmail");
    const savedPassword = localStorage.getItem("savedPassword");

    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const validateForm = () => {
    let formErrors = {};
    let isValid = true;

    if (!email) {
      formErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      formErrors.email = "Invalid email format";
      isValid = false;
    }

    if (!password) {
      formErrors.password = "Password is required";
      isValid = false;
    }

    setErrors(formErrors);
    return isValid;
  };

  const submitLogin = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (rememberMe) {
      localStorage.setItem("savedEmail", email);
      localStorage.setItem("savedPassword", password);
    } else {
      localStorage.removeItem("savedEmail");
      localStorage.removeItem("savedPassword");
    }

    setLoading(true);

    try {
      const response = await axios.post(`${MAIN_URL}/api/auth/login`, {
        email,
        password_hash: password,
      });
      const { token, user } = response.data;

      localStorage.setItem("token", token);
      setAccess(user?.role?.system_role_name);
      login(buildCoreSessionUser(user));
      toast.success("Login successful!");
      navigate("/");
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      const apiMessage = data?.message;

      if (data?.errors) {
        toast.error(apiMessage || "Validation errors occurred.");
        setErrors(data.errors);
      } else if (apiMessage) {
        toast.error(apiMessage);
      } else if (status === 500) {
        toast.error("Server error occurred. Please try again later.");
      } else if (!error.response) {
        toast.error("Unable to reach the server. Please try again.");
      } else {
        toast.error("Login failed. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      navigate("/");
    }
  }, [isLoggedIn]);

  return (
    <AuthShell
      title="Welcome to CORE"
      subtitle={title}
      appName={appName}
      description={description}
    >
      <form onSubmit={submitLogin} noValidate>
        <div className="ems-auth-field">
          <label htmlFor="auth-email">Email Address</label>
          <input
            id="auth-email"
            type="text"
            className="ems-fm-input"
            placeholder="name@company.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: "" })); }}
          />
          {errors.email && <span className="ems-fm-err">{errors.email}</span>}
        </div>

        <div className="ems-auth-field">
          <label htmlFor="auth-pw">Password</label>
          <div className="ems-auth-pw">
            <input
              id="auth-pw"
              type={showPassword ? "text" : "password"}
              className="ems-fm-input"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setErrors((p) => ({ ...p, password: "" })); }}
            />
            <button
              type="button"
              className="ems-auth-pw-eye"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && <span className="ems-fm-err">{errors.password}</span>}
        </div>

        <div className="ems-auth-meta">
          <label className="ems-auth-remember">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            Remember me
          </label>
          <Link to="/forgot-password" className="ems-auth-forgot">Forgot password?</Link>
        </div>

        <div className="ems-auth-actions">
          <button
            type="submit"
            className="ems-btn ems-primary"
            disabled={loading}
          >
            {loading ? <><SpinSVG /> Signing in…</> : "Sign In"}
          </button>
        </div>
      </form>
    </AuthShell>
  );
}

export default Login;
